import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

export type BadgeTone = 'accent' | 'warning' | 'success' | 'danger' | 'muted';

export interface SearchableOption {
  value: number | string;
  label: string;
  sublabel?: string;
  badge?: { text: string; tone?: BadgeTone };
}

const BADGE_CLASS: Record<BadgeTone, string> = {
  accent: 'bg-accent/15 text-accent',
  warning: 'bg-warning/15 text-warning',
  success: 'bg-success/15 text-success',
  danger: 'bg-danger/15 text-danger',
  muted: 'bg-white/10 text-[var(--text-muted)]',
};

interface Props {
  options: SearchableOption[];
  value: number | string | '';
  onChange: (value: number | string | '') => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  leftIcon?: ReactNode;
  /** Compact one-line menu rows (sublabel shown on the right) — for filter bars. */
  dense?: boolean;
  /** Show options in the order given (e.g. newest first) instead of A→Z when nothing is typed. */
  keepOrder?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  leftIcon,
  dense,
  keepOrder,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      // The menu is portaled to <body>, so a click on it isn't inside wrapRef —
      // check the menu ref too, otherwise picking an option would close first.
      if (
        wrapRef.current && !wrapRef.current.contains(t) &&
        (!listRef.current || !listRef.current.contains(t))
      ) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // Portaled menu is fixed-positioned to the field, so it escapes any modal's
  // overflow clipping. Recompute on open and while scrolling/resizing.
  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    function place() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return keepOrder ? options : [...options].sort((a, b) => a.label.localeCompare(b.label));
    }
    const scored = options
      .map((o) => {
        const label = o.label.toLowerCase();
        const sub = (o.sublabel ?? '').toLowerCase();
        let score = 0;
        if (label === q) score = 6;
        else if (label.startsWith(q)) score = 5;
        else {
          const words = label.split(/[\s\-,·]+/);
          if (words.some((w) => w.startsWith(q))) score = 4;
          else if (label.includes(q)) score = 3;
          else if (sub.startsWith(q)) score = 2;
          else if (sub.includes(q)) score = 1;
          else return null;
        }
        return { o, score };
      })
      .filter((x): x is { o: SearchableOption; score: number } => x !== null);
    scored.sort(
      (a, b) => b.score - a.score || a.o.label.localeCompare(b.o.label),
    );
    return scored.map((s) => s.o);
  }, [options, query, keepOrder]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-idx="${highlight}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  function pick(opt: SearchableOption) {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
  }

  const displayText = open ? query : selected?.label ?? '';

  return (
    <div ref={wrapRef} className={clsx('relative', className)}>
      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-[var(--text-muted)]">
            {leftIcon}
          </span>
        )}
        <input
          type="text"
          className={clsx(
            'w-full rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] py-2 pr-8 text-sm text-[var(--text-primary)] outline-none focus:border-accent disabled:opacity-60',
            leftIcon ? 'pl-8' : 'pl-3',
          )}
          value={displayText}
          placeholder={placeholder ?? 'Search…'}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const p = filtered[highlight];
              if (p) pick(p);
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        {selected && !open && !disabled && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              setQuery('');
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-lg leading-none text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            title="Clear"
            tabIndex={-1}
          >
            ×
          </button>
        )}
        {!selected && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--text-muted)]">
            ▾
          </span>
        )}
      </div>
      {open && menuPos && createPortal(
        <ul
          ref={listRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}
          className="z-[600] max-h-60 overflow-y-auto rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] shadow-lg"
        >
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-[var(--text-muted)]">
              No matches
            </li>
          )}
          {filtered.map((o, i) => (
            <li
              key={o.value}
              data-idx={i}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
              onMouseEnter={() => setHighlight(i)}
              title={dense ? o.label : undefined}
              className={clsx(
                'cursor-pointer',
                dense ? 'flex items-center justify-between gap-2 px-2.5 py-1.5 text-[12px]' : 'px-3 py-2 text-sm',
                i === highlight
                  ? 'bg-accent/15 text-accent'
                  : 'text-[var(--text-primary)] hover:bg-[var(--input-bg)]',
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className={clsx('font-medium', dense && 'truncate', dense && o.value === value && 'font-bold text-accent')}>{o.label}</span>
                {o.badge && (
                  <span
                    className={clsx(
                      'rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                      BADGE_CLASS[o.badge.tone ?? 'accent'],
                    )}
                  >
                    {o.badge.text}
                  </span>
                )}
              </div>
              {o.sublabel && (
                <div className={clsx('text-[10px] text-[var(--text-muted)]', dense && 'shrink-0 tabular-nums')}>
                  {o.sublabel}
                </div>
              )}
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
